import buffer from '@turf/buffer'
import envelope from '@turf/envelope'
import { featureCollection } from '@turf/helpers'
import pointOnFeature from '@turf/point-on-feature'
import $ from 'jquery'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import OLGeometry from 'ol/geom/Geometry'
import OLGeomLineString from 'ol/geom/LineString'
import { fromExtent as OLPolygonFromExtent } from 'ol/geom/Polygon'
import { transform as OLProjTransform, transformExtent as OLProjTransformExtent } from 'ol/proj'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { ShowWhatsHere } from 'drawer/tabs/info/WhatsHereUtils'
import { targetGetCenter } from 'utils/target'

const PoziZoom = memoize(function () {
  // Adapters
  const adapters = {
    zoomToExtent: function (extent, preventZoomIn, callback) {
      return zoomToExtent(extent, preventZoomIn, callback)
    },
    zoomToTarget: function (xy, preventZoomIn, callback) {
      return zoomToTarget(xy, preventZoomIn, callback)
    },
    zoomMap: function (geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback) {
      return zoomMap(geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback)
    },
    zoomToGeoJSON: function (geojson, zoomPadding, bSelectCenterPoint, callback) {
      return zoomToGeoJSON(geojson, zoomPadding, bSelectCenterPoint, callback)
    },
    zoomPanTo: function (geom, preventZoomIn, callback) {
      return zoomPanTo(geom, preventZoomIn, callback)
    },
    centerOnTarget: function () {
      return centerOnTarget()
    }
  }

  const app = PoziUtils.getApp()

  /**
   * Zoom map to extent
   */
  const zoomToExtent = function (extent, preventZoomIn = false, callback) {
    console.debug('zoomToExtent', extent, preventZoomIn)

    if (!extent) {
      console.error('No coords found.')
      return
    }

    if (!(extent instanceof Array) || extent.length !== 4) {
      console.error('Invalid extent', extent)
      return
    }

    // Make sure that the coordinates are numbers, as they sometimes get returned from the server
    // as strings
    try {
      extent = extent.map(coordinate => Number(coordinate))
    } catch (error) {
      console.log('zoomToExtent - cannot convert coordinates to number. Expect problemns', extent)
    }

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'extent',
      action: 'Zoom to'
    })

    // Remove target
    Model.unset('app.state.target') // Clear any old targets.
    HistoryUpdate()

    const transformedExtent = OLProjTransformExtent(extent, 'EPSG:4326', 'EPSG:3857')
    const extentPolygon = OLPolygonFromExtent(transformedExtent)

    zoomPanTo(extentPolygon, preventZoomIn, callback)
  }

  /**
   * Zoom map to lng,lat and set target state.
   */
  const zoomToTarget = function (xy, preventZoomIn = false, callback) {
    console.debug('zoomToTarget', xy, preventZoomIn)

    if (!xy || xy.length <= 0) {
      console.error('No coords found.')
      return
    }

    if (xy.length < 2) {
      console.error('Invalid coordinates', xy)
      return
    }

    console.debug('zoomToTarget', xy)

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'target',
      action: 'Zoom to'
    })

    // Remove target
    Model.unset('app.state.target') // Clear any old targets.
    HistoryUpdate()

    // Make sure that the coordinates are numbers, as they sometimes get returned from the server
    // as strings
    try {
      xy = xy.map(coordinate => Number(coordinate))
    } catch (error) {
      console.log('zoomToTarget - cannot convert coordinates to number. Expect problemns', xy)
    }

    // console.debug("Option selected:"+xy);
    const [lng, lat] = xy
    const diagonal = new OLGeomLineString(
      $.map([[lng - 0.001, lat - 0.001], [lng + 0.001, lat + 0.001]], function (pt) {
        return [OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')]
      })
    )

    zoomPanTo(diagonal, preventZoomIn, callback)
  }

  /**
   * Zoom on geojson
   */
  const zoomToGeoJSON = function (geojson, zoomPadding, bSelectCenterPoint, callback) {
    console.debug('zoomToGeoJSON')
    return new Promise(function (resolve, reject) {
      return zoomMap(geojson, zoomPadding, bSelectCenterPoint, resolve, reject, callback)
    })
  }

  const zoomMap = function (geojson, zoomPadding = 0, bSelectCenterPoint, resolve, reject, callback) {
    console.debug('zoomMap')

    let message = ''
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      message = 'PoziZoom: Unable to find any features in geojson'
      return reject(message)
    }

    const isSinglePoint = geojson.features.length === 1 && geojson.features[0]?.geometry?.type === 'Point'

    console.debug('zoomMap - geojson is point?', isSinglePoint)

    if (isSinglePoint) {
      const xy = geojson.features[0].geometry.coordinates
      zoomToTarget(xy, false, callback)
    } else {
      const enveloped = envelope(geojson)
      console.debug('zoomMap - enveloped', enveloped)
      const cornerA = enveloped.geometry.coordinates[0][0]
      const cornerB = enveloped.geometry.coordinates[0][2]

      const buffered = buffer(geojson, -0.0001, { units: 'kilometers' })

      console.debug('zoomMap - buffered', buffered)

      const pointOnSurface = pointOnFeature(buffered)

      // const centerOfMass = centerOfMass(geojson)
      // const midpoint = midpoint(pointOnSurface, centerOfMass)
      geojson.xy = pointOnSurface.geometry.coordinates

      const diagonal = new OLGeomLineString(
        $.map([[cornerA[0] - zoomPadding, cornerA[1] - zoomPadding], [cornerB[0] + zoomPadding, cornerB[1] + zoomPadding]], function (pt) {
          return [OLProjTransform(pt, 'EPSG:4326', 'EPSG:3857')]
        })
      )

      zoomPanTo(diagonal, false, callback)
    }

    if (bSelectCenterPoint) {
      const multiPointFeatures = []
      for (const key in geojson.features) {
        const feature = geojson.features[key]
        const buffered2 = buffer(feature, -0.0001, { units: 'kilometers' }) || feature
        const featureOnSurface2 = pointOnFeature(buffered2)
        multiPointFeatures.push(featureOnSurface2)
      }
      const fc = featureCollection(multiPointFeatures)
      ShowWhatsHere(fc)

      // TODO: select marker should move to it's own module rather than global app object.
      // Add green dot where the address point is and trigger property retrieval code.
      // app.selectMarker.setPosition(app.transformFrom4326(geojson.xy)) // Triggers app.selectMarker.on('change:position' ...
      // PoziMap().showMarker()
    }

    if (geojson.features.length > 1) {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'map',
        action: 'Zoom to',
        additionalInfo: 'multi location'
      })
    } else {
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: 'map',
        action: 'Zoom to',
        additionalInfo: 'single location'
      })
    }

    resolve('PoziZoom: Complete.')
  }

  // Animated zoom / panning
  const zoomPanTo = function (geom, preventZoomIn, callback) {
    console.debug('zoomPanTo')

    let _cb = callback
    if (isNil(_cb)) {
      _cb = _zoomComplete
    }

    if (!geom) {
      geom = app.map.getSize()
    }

    const view = app.map.getView()

    // Screen resolution-sensitive padding
    // https://openlayers.org/en/latest/apidoc/ol.View.html#fit

    // Somehow OL on mobile does not seem to interpret the padding as pixels
    const currentWindowMode = Model.get('app.event.window.mode', '')
    const base = currentWindowMode === 'mobile' ? 5 : 75
    const padding = [base, base, base, base]

    // For points default to zoom level 16 to give local context
    const geomIsPoint = geom instanceof OLGeometry && geom.getType() === 'Point'
    let maxZoom = geomIsPoint ? 16 : 20

    // Abuse 'preventZoomIn' argument to allow for a custom maxZoom
    if (preventZoomIn === true) {
      maxZoom = view.getZoom()
    } else if (typeof preventZoomIn === 'number') {
      maxZoom = preventZoomIn
    }

    // Fitting the map view to the passed extent
    view.fit(geom, {
      padding,
      maxZoom, // When a zoom/pan is requested.
      duration: 1000,
      callback: _cb
    })
  }

  const _zoomComplete = function (bSuccess) {
    console.debug('Zoom completed (default callback)', bSuccess)
  }

  // posEnum: upper, middle (default), lower
  const centerOnTarget = function () {
    console.debug('centerOnTarget')

    const view = app.map.getView()
    const args = {}
    const center = targetGetCenter()
    if (isNil(center)) {
      return
    }
    const centerCoords = get(center, 'geometry.coordinates', null)
    if (!isArray(centerCoords)) {
      return
    }
    args.center = OLProjTransform(centerCoords, 'EPSG:4326', 'EPSG:3857')
    view.animate(args) // animate is less jaring than setCenter.
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziZoom
